import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import { validateField } from "../../utils/validation";
import { validateSingleCertification } from "../../utils/certificateValidation";
import { validateSingleExperience } from "../../utils/experienceValidate";
import { validateSingleProject } from "../../utils/projectValidate";
// Comprehensive interfaces for all data types
interface Skill {
  id?: string;
  skill_name: string;
  proficiency_level: string;
  years_of_experience?: number;
}

export interface WorkExperience {
  id?: string;
  job_title: string;
  company_name: string;
  location?: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  description?: string;
  responsibilities?: string[];
  key_achievements?: string[];
}

interface Education {
  id?: string;
  degree: string;
  field_of_study?: string;
  institution: string;
  graduation_year?: number;
  gpa?: number;
}

export interface Project {
  id?: string;
  name: string;
  description?: string;
  role?: string;
  technologies: string[];
  url?: string;
  start_date?: string;
  end_date?: string;
  is_ongoing?: boolean;
  achievements?: string[];
}

interface Certification {
  id?: string;
  name: string;
  issuing_organization: string;
  issue_date?: string;
  expiration_date?: string;
  credential_id?: string;
  credential_url?: string;
  does_not_expire?: boolean;
}

interface Language {
  id?: string;
  language: string;
  proficiency: string;
  is_native?: boolean;
}

interface Award {
  id?: string;
  title: string;
  organization: string;
  date?: string;
  description?: string;
}

interface ProfileFormData {
  // Personal Information
  first_name: string;
  last_name: string;
  country_code: string;
  phone: string;
  location: string;
  city: string;
  state: string;
  country: string;

  // Professional Summary
  professional_summary: string;
  current_job_title: string;
  current_company: string;
  years_of_experience: number;

  // Work Preferences
  expected_salary: string;
  availability: string;
  preferred_work_type: string;
  willing_to_relocate: boolean;
  work_authorization: string;

  // Social Links
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
  personal_website: string;

  // Comprehensive Data Arrays
  skills: Skill[];
  work_experiences: WorkExperience[];
  education: Education[];
  projects: Project[];
  certifications: Certification[];
  languages: Language[];
  awards: Award[];
}

interface CountryOption {
  id: string;
  code: string;
  name: string;
  phone_code?: string;
}

interface StateOption {
  id: string;
  code: string;
  name: string;
}

interface CityOption {
  id: string;
  name: string;
}

// Helper function to convert ISO date to YYYY-MM format
const formatDateForMonthInput = (
  dateString: string | null | undefined,
): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  } catch {
    return "";
  }
};

const currentMonth = new Date().toISOString().slice(0, 7);
const monthInputPlaceholder = "mm/yyyy";

interface MonthInputProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className: string;
  min?: string;
  max?: string;
}

const MonthInput: React.FC<MonthInputProps> = ({
  value,
  onChange,
  className,
  min,
  max,
}) => {
  const [inputType, setInputType] = useState<"text" | "month">(
    value ? "month" : "text",
  );

  useEffect(() => {
    setInputType(value ? "month" : "text");
  }, [value]);

  return (
    <input
      type={inputType}
      value={value}
      min={min}
      max={max}
      placeholder={monthInputPlaceholder}
      inputMode="numeric"
      className={className}
      onFocus={() => setInputType("month")}
      onBlur={() => {
        if (!value) {
          setInputType("text");
        }
      }}
      onChange={onChange}
    />
  );
};

const buildLocationFromParts = (
  city?: string,
  state?: string,
  country?: string,
): string => {
  return [city, state, country]
    .filter((part) => part && part.trim())
    .join(", ");
};

const parseLocationParts = (
  location?: string,
): { city?: string; state?: string; country?: string } => {
  if (!location) return { city: "", state: "", country: "" };
  const parts = location.split(", ");
  if (parts.length === 1) {
    return { city: parts[0] || "", state: "", country: "" };
  }

  if (parts.length === 2) {
    return {
      city: parts[0] || "",
      state: "",
      country: parts[1] || "",
    };
  }

  return {
    city: parts[0] || "",
    state: parts[1] || "",
    country: parts.slice(2).join(", ") || "",
  };
};

const sanitizePhoneInput = (value: string): string => {
  return value.replace(/\D/g, "");
};

const normalizePhoneForSave = (value: string): string => {
  return sanitizePhoneInput(value).trim();
};

const sanitizeCountryCodeInput = (value: string): string => {
  const codeCharsOnly = value.replace(/[^\d+]/g, "");
  return codeCharsOnly.replace(/(?!^)\+/g, "");
};

const hasRequiredEducation = (education: Education[]): boolean => {
  return education.some(
    (edu) => edu.degree.trim() !== "" && edu.institution.trim() !== "",
  );
};

const CandidateProfileEditNew: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("personal");
  const [educationError, setEducationError] = useState("");

  const personalInfoRef = React.useRef<any>();

  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: "",
    last_name: "",
    country_code: "",
    phone: "",
    location: "",
    city: "",
    state: "",
    country: "",
    professional_summary: "",
    current_job_title: "",
    current_company: "",
    years_of_experience: 0,
    expected_salary: "",
    availability: "immediately",
    preferred_work_type: "full-time",
    willing_to_relocate: false,
    work_authorization: "authorized",
    linkedin_url: "",
    github_url: "",
    portfolio_url: "",
    personal_website: "",
    skills: [],
    work_experiences: [],
    education: [],
    projects: [],
    certifications: [],
    languages: [],
    awards: [],
  });

  // Add a new state for showing confirmation modal
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const profile = await apiService.getCandidateProfile();
      console.log("Loaded profile data:", profile);
      console.log("Work experience fields:", {
        work_experiences: (profile as any).work_experiences,
        work_experience: (profile as any).work_experience,
        experiences: (profile as any).experiences,
      });

      if (profile) {
        const savedLocation = profile.location || "";
        const parsedLocation = parseLocationParts(savedLocation);

        const mappedData: ProfileFormData = {
          first_name: String(profile.first_name || user?.first_name || ""),
          last_name: String(profile.last_name || user?.last_name || ""),
          country_code: String((profile as any).country_code || ""),
          phone: String(profile.phone || ""),
          location: String(
            profile.location ||
              buildLocationFromParts(
                profile.city || parsedLocation.city,
                profile.state || parsedLocation.state,
                profile.country || parsedLocation.country,
              ) ||
              "",
          ),
          city: String(profile.city || parsedLocation.city || ""),
          state: String(profile.state || parsedLocation.state || ""),
          country: String(profile.country || parsedLocation.country || ""),
          professional_summary: String(profile.professional_summary || ""),
          current_job_title: String(profile.current_job_title || ""),
          current_company: String(profile.current_company || ""),
          years_of_experience: profile.years_of_experience || 0,
          expected_salary: String(profile.expected_salary || ""),
          availability: profile.availability || "immediately",
          preferred_work_type: profile.preferred_work_type || "full-time",
          willing_to_relocate: profile.willing_to_relocate || false,
          work_authorization: profile.work_authorization || "authorized",
          linkedin_url: String(profile.linkedin_url || ""),
          github_url: String(profile.github_url || ""),
          portfolio_url: String(profile.portfolio_url || ""),
          personal_website: String(profile.personal_website || ""),
          skills:
            profile.skills?.map((s) => ({
              id: s.id,
              skill_name: s.skill_name,
              proficiency_level: s.proficiency_level || "intermediate",
              years_of_experience: s.years_of_experience,
            })) || [],
          work_experiences:
            (
              (profile as any).work_experiences ||
              (profile as any).work_experience ||
              (profile as any).experiences
            )?.map((exp: any) => ({
              id: exp.id,
              job_title: exp.job_title,
              company_name: exp.company_name,
              location: exp.location,
              start_date: exp.start_date || "",
              end_date: exp.end_date,
              is_current: exp.is_current,
              description: exp.description,
              responsibilities: exp.responsibilities || [],
              key_achievements: exp.achievements || [],
            })) || [],
          education:
            profile.education?.map((edu) => ({
              id: edu.id,
              degree: edu.degree_name || edu.degree_type || "",
              field_of_study: edu.field_of_study,
              institution: edu.institution_name,
              graduation_year: edu.graduation_year,
              gpa: edu.gpa,
            })) || [],
          projects:
            profile.projects?.map((proj, index) => ({
              id: `project-${index}`,
              name: proj.name,
              description: proj.description,
              role: proj.role,
              technologies: proj.technologies || [],
              url: proj.url,
              start_date: proj.start_date,
              end_date: proj.end_date,
              is_ongoing: proj.is_ongoing,
              achievements: proj.achievements || [],
            })) || [],
          certifications:
            profile.certifications?.map((cert, index) => ({
              id: `cert-${index}`,
              name: cert.name,
              issuing_organization: cert.issuing_organization,
              issue_date: cert.issue_date,
              expiration_date: cert.expiration_date,
              credential_id: cert.credential_id,
              credential_url: cert.credential_url,
              does_not_expire: cert.does_not_expire,
            })) || [],
          languages:
            profile.languages?.map((lang: any, index: number) => ({
              id: `lang-${index}`,
              language: lang.language || lang.name || "",
              proficiency: lang.proficiency || "intermediate",
              is_native: lang.is_native || false,
            })) || [],
          awards:
            profile.awards?.map((award, index) => ({
              id: `award-${index}`,
              title: award.title,
              organization: award.organization,
              date: award.date,
              description: award.description,
            })) || [],
        };

        console.log("Mapped form data:", mappedData);
        console.log(
          "Work experiences count:",
          mappedData.work_experiences.length,
        );
        console.log("Projects count:", mappedData.projects.length);
        console.log("Certifications count:", mappedData.certifications.length);
        console.log("Languages count:", mappedData.languages.length);
        console.log("Awards count:", mappedData.awards.length);

        setFormData(mappedData);
      }
    } catch (error: any) {
      console.error("Error loading profile data:", error);
      setError("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Helper functions for array operations
  const addItem = (section: keyof ProfileFormData, newItem: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: [
        ...(prev[section] as any[]),
        { ...newItem, id: Date.now().toString() },
      ],
    }));
    if (section === "education") {
      setEducationError("");
    }
  };

  const updateItem = (
    section: keyof ProfileFormData,
    index: number,
    updatedItem: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: (prev[section] as any[]).map((item, i) =>
        i === index ? updatedItem : item,
      ),
    }));
    if (section === "education") {
      setEducationError("");
    }
  };

  const deleteItem = (section: keyof ProfileFormData, index: number) => {
    setFormData((prev) => ({
      ...prev,
      [section]: (prev[section] as any[]).filter((_, i) => i !== index),
    }));
    if (section === "education") {
      setEducationError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationResults: { [key: string]: boolean } = {};

    if (personalInfoRef.current) {
      const isValid = personalInfoRef.current.validate();
      validationResults.personal = isValid;
    }

    const isEducationValid = hasRequiredEducation(formData.education);
    validationResults.education = isEducationValid;
    setEducationError(
      isEducationValid
        ? ""
        : "Education is required. Add at least one education entry with degree and institution.",
    );

    // Check if any validation failed
    const hasErrors = Object.values(validationResults).some(
      (result) => result === false,
    );

    if (hasErrors) {
      // Find first tab with errors and switch to it
      const firstInvalidTab = Object.entries(validationResults).find(
        ([_, isValid]) => !isValid,
      );
      if (firstInvalidTab) {
        setActiveTab(firstInvalidTab[0]);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return; // STOP HERE - don't proceed with saving
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Format the data to match the backend API expectations
      let cleanedPhone = formData.phone;
      if (cleanedPhone) {
        cleanedPhone = normalizePhoneForSave(cleanedPhone);
      }

      const derivedLocation = buildLocationFromParts(
        formData.city,
        formData.state,
        formData.country,
      );
      const profileData = {
        // Basic profile information
        first_name: formData.first_name,
        last_name: formData.last_name,
        country_code: formData.country_code || null,
        phone: cleanedPhone,
        location: derivedLocation || formData.location,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || null,
        professional_summary: formData.professional_summary,
        current_job_title: formData.current_job_title,
        current_company: formData.current_company,
        years_of_experience: formData.years_of_experience,
        expected_salary: formData.expected_salary
          ? parseInt(formData.expected_salary)
          : null,
        availability: formData.availability,
        preferred_work_type: formData.preferred_work_type,
        willing_to_relocate: formData.willing_to_relocate,
        work_authorization: formData.work_authorization,
        linkedin_url: formData.linkedin_url,
        github_url: formData.github_url,
        portfolio_url: formData.portfolio_url,
        personal_website: formData.personal_website,

        // Comprehensive data arrays - format for backend
        skills: formData.skills.map((skill) => ({
          skill_name: skill.skill_name,
          proficiency_level: skill.proficiency_level,
          years_of_experience: skill.years_of_experience,
        })),

        work_experience: formData.work_experiences.map((exp) => ({
          job_title: exp.job_title,
          company_name: exp.company_name,
          location: exp.location,
          start_date: exp.start_date,
          end_date: exp.end_date,
          is_current: exp.is_current,
          description: exp.description,
          responsibilities: exp.responsibilities || [],
          achievements: exp.key_achievements || [],
        })),

        education: formData.education.map((edu) => ({
          degree_name: edu.degree,
          degree_type: edu.degree,
          field_of_study: edu.field_of_study,
          institution_name: edu.institution,
          graduation_year: edu.graduation_year,
          gpa: edu.gpa,
        })),

        projects: formData.projects.map((project) => ({
          name: project.name,
          description: project.description,
          role: project.role,
          technologies: project.technologies,
          url: project.url,
          start_date: project.start_date,
          end_date: project.end_date,
          is_ongoing: project.is_ongoing,
          achievements: project.achievements,
        })),

        certifications: formData.certifications.map((cert) => ({
          name: cert.name,
          issuing_organization: cert.issuing_organization,
          issue_date: cert.issue_date,
          expiration_date: cert.expiration_date,
          credential_id: cert.credential_id,
          credential_url: cert.credential_url,
          does_not_expire: cert.does_not_expire,
        })),

        languages: formData.languages.map((lang) => ({
          name: (lang as any).language, // Backend expects 'name', frontend interface uses 'language'
          proficiency: lang.proficiency,
          is_native: lang.is_native,
        })),

        awards: formData.awards.map((award) => ({
          title: award.title,
          organization: award.organization,
          date: award.date,
          description: award.description,
        })),
      };

      console.log("Sending profile data:", profileData);
      await apiService.updateCandidateProfile(profileData);

      // Show confirmation modal instead of auto-redirect
      setShowSaveConfirmation(true);
    } catch (error: any) {
      console.error("Error updating profile:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to update profile",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfirmation = () => {
    setShowSaveConfirmation(false);
    // Clear any cached data to ensure fresh load
    sessionStorage.removeItem("profile_cache");
    localStorage.removeItem("profile_cache");
    navigate("/candidate/profile");
  };

  const tabs = [
    { id: "personal", name: "Personal Info", icon: "👤" },
    { id: "skills", name: "Skills", icon: "🛠️" },
    { id: "experience", name: "Experience", icon: "💼" },
    { id: "education", name: "Education", icon: "🎓" },
    { id: "projects", name: "Projects", icon: "🚀" },
    { id: "certifications", name: "Certifications", icon: "📜" },
    { id: "languages", name: "Languages & Awards", icon: "🌐" },
    { id: "preferences", name: "Preferences", icon: "⚙️" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Edit Profile</h1>
          <p className="mt-2 text-gray-600">
            Update your professional information and preferences
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          {/* Tab Navigation - Mobile Responsive */}
          <div className="border-b border-gray-200">
            <nav
              className="flex overflow-x-auto scrollbar-hide px-4 sm:px-6"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                  .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                  }
                `,
                }}
              />
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 py-3 px-3 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-emerald-500 text-emerald-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span className="mr-1 sm:mr-2">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">
                    {tab.name.split(" ")[0]}
                    {tab.name.includes("&")
                      ? " & " + tab.name.split("& ")[1]
                      : ""}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6">
            {/* Personal Info Tab */}
            {activeTab === "personal" && (
              <PersonalInfoTab
                ref={personalInfoRef}
                formData={formData}
                setFormData={setFormData}
              />
            )}

            {/* Skills Tab */}
            {activeTab === "skills" && (
              <SkillsTab
                skills={formData.skills}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItem}
              />
            )}

            {/* Experience Tab */}
            {activeTab === "experience" && (
              <ExperienceTab
                experiences={formData.work_experiences}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItem}
              />
            )}

            {/* Education Tab */}
            {activeTab === "education" && (
              <EducationTab
                education={formData.education}
                educationError={educationError}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItem}
              />
            )}

            {/* Projects Tab */}
            {activeTab === "projects" && (
              <ProjectsTab
                projects={formData.projects}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItem}
              />
            )}

            {/* Certifications Tab */}
            {activeTab === "certifications" && (
              <CertificationsTab
                certifications={formData.certifications}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItem}
              />
            )}

            {/* Languages & Awards Tab */}
            {activeTab === "languages" && (
              <LanguagesAwardsTab
                languages={formData.languages}
                awards={formData.awards}
                addItem={addItem}
                updateItem={updateItem}
                deleteItem={deleteItem}
              />
            )}

            {/* Preferences Tab */}
            {activeTab === "preferences" && (
              <PreferencesTab formData={formData} setFormData={setFormData} />
            )}

            {/* Submit Button */}
            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => navigate("/candidate/profile")}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showSaveConfirmation && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Profile Updated</h2>
            <p>Your profile has been updated successfully!</p>
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSaveConfirmation}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add forwardRef to PersonalInfoTab
const PersonalInfoTab = React.forwardRef<any, any>(
  ({ formData, setFormData }, ref) => {
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [touchedFields, setTouchedFields] = React.useState<Set<string>>(
      new Set(),
    );
    const [countries, setCountries] = React.useState<CountryOption[]>([]);
    const [states, setStates] = React.useState<StateOption[]>([]);
    const [cities, setCities] = React.useState<CityOption[]>([]);
    const [selectedCountryCode, setSelectedCountryCode] = React.useState("");
    const [selectedStateCode, setSelectedStateCode] = React.useState("");
    const [loadingLocations, setLoadingLocations] = React.useState({
      countries: false,
      states: false,
      cities: false,
    });
    const [locationLoadError, setLocationLoadError] = React.useState("");
    const initializedLocationRef = React.useRef(false);

    // Expose validation method to parent
    React.useImperativeHandle(ref, () => ({
      validate: () => {
        const newErrors: Record<string, string> = {};
        const fieldsToValidate = [
          "first_name",
          "last_name",
          "country_code",
          "phone",
          "location",
          "professional_summary",
          "current_job_title",
          "current_company",
          "years_of_experience",
          "linkedin_url",
          "github_url",
          "portfolio_url",
          "personal_website",
        ];

        fieldsToValidate.forEach((field) => {
          const error = validateField(field, formData[field]);
          if (error) {
            newErrors[field] = error;
          }
        });

        setErrors(newErrors);

        // Mark all fields as touched
        const allTouched = new Set(touchedFields);
        fieldsToValidate.forEach((field) => allTouched.add(field));
        setTouchedFields(allTouched);

        return Object.keys(newErrors).length === 0;
      },
    }));

    const handleChange = (field: string, value: any) => {
      const nextValue =
        field === "phone"
          ? sanitizePhoneInput(String(value))
          : field === "country_code"
            ? sanitizeCountryCodeInput(String(value))
            : value;

      setErrors((prev) => ({ ...prev, [field]: "" }));
      const error = validateField(field, nextValue);
      if (error) {
        setErrors((prev) => ({ ...prev, [field]: error }));
      }
      setFormData((prev: any) => ({ ...prev, [field]: nextValue }));
    };

    const updateLocationFields = (
      updates: Partial<Pick<ProfileFormData, "city" | "state" | "country">>,
    ) => {
      const nextLocation = buildLocationFromParts(
        updates.city ?? formData.city,
        updates.state ?? formData.state,
        updates.country ?? formData.country,
      );

      setTouchedFields((prev) => new Set(prev).add("location"));
      setErrors((prev) => ({
        ...prev,
        location: validateField("location", nextLocation),
      }));
      setFormData((prev: ProfileFormData) => ({
        ...prev,
        ...updates,
        location: nextLocation,
      }));
    };

    const loadCountries = React.useCallback(async () => {
      setLoadingLocations((prev) => ({ ...prev, countries: true }));
      setLocationLoadError("");
      try {
        const countriesData = await apiService.getCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error("Failed to load countries:", error);
        setLocationLoadError("Failed to load countries");
      } finally {
        setLoadingLocations((prev) => ({ ...prev, countries: false }));
      }
    }, []);

    const loadStatesForCountry = React.useCallback(
      async (countryCode: string) => {
        setLoadingLocations((prev) => ({ ...prev, states: true }));
        setLocationLoadError("");
        try {
          const statesData = await apiService.getStates(countryCode);
          setStates(statesData);
          return statesData as StateOption[];
        } catch (error) {
          console.error("Failed to load states:", error);
          setStates([]);
          setLocationLoadError("Failed to load states");
          return [];
        } finally {
          setLoadingLocations((prev) => ({ ...prev, states: false }));
        }
      },
      [],
    );

    const loadCitiesForState = React.useCallback(
      async (countryCode: string, stateCode: string) => {
        setLoadingLocations((prev) => ({ ...prev, cities: true }));
        setLocationLoadError("");
        try {
          const citiesData = await apiService.getCities(countryCode, stateCode);
          setCities(citiesData);
          return citiesData as CityOption[];
        } catch (error) {
          console.error("Failed to load cities:", error);
          setCities([]);
          setLocationLoadError("Failed to load cities");
          return [];
        } finally {
          setLoadingLocations((prev) => ({ ...prev, cities: false }));
        }
      },
      [],
    );

    const loadCitiesForCountry = React.useCallback(
      async (countryCode: string) => {
        setLoadingLocations((prev) => ({ ...prev, cities: true }));
        setLocationLoadError("");
        try {
          const citiesData = await apiService.getCitiesForCountry(countryCode);
          setCities(citiesData);
          return citiesData as CityOption[];
        } catch (error) {
          console.error("Failed to load cities:", error);
          setCities([]);
          setLocationLoadError("Failed to load cities");
          return [];
        } finally {
          setLoadingLocations((prev) => ({ ...prev, cities: false }));
        }
      },
      [],
    );

    React.useEffect(() => {
      loadCountries();
    }, [loadCountries]);

    React.useEffect(() => {
      if (
        initializedLocationRef.current ||
        !countries.length ||
        !formData.country
      ) {
        return;
      }

      const initializeSavedLocation = async () => {
        const matchedCountry = countries.find(
          (country) =>
            country.name.toLowerCase() === formData.country.toLowerCase(),
        );

        if (!matchedCountry) {
          initializedLocationRef.current = true;
          return;
        }

        initializedLocationRef.current = true;
        setSelectedCountryCode(matchedCountry.code);
        if (!formData.country_code && matchedCountry.phone_code) {
          setFormData((prev: ProfileFormData) => ({
            ...prev,
            country_code: matchedCountry.phone_code || "",
          }));
        }
        const statesData = await loadStatesForCountry(matchedCountry.code);

        if (statesData.length > 0) {
          const matchedState = statesData.find(
            (state) =>
              state.name.toLowerCase() === (formData.state || "").toLowerCase(),
          );

          if (matchedState) {
            setSelectedStateCode(matchedState.code);
            await loadCitiesForState(matchedCountry.code, matchedState.code);
          }
        } else {
          await loadCitiesForCountry(matchedCountry.code);
        }

        initializedLocationRef.current = true;
      };

      initializeSavedLocation();
    }, [
      countries,
      formData.country_code,
      formData.country,
      formData.state,
      loadCitiesForCountry,
      loadCitiesForState,
      loadStatesForCountry,
      setFormData,
    ]);

    const handleBlur = (field: string, value: any) => {
      setTouchedFields((prev) => new Set(prev).add(field));
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    };

    const getInputClass = (field: string) => {
      const baseClass =
        "mt-1 block w-full rounded-md shadow-sm focus:ring-emerald-500";
      const showError = errors[field] && touchedFields.has(field);
      return showError
        ? `${baseClass} border-red-300 focus:border-red-500 focus:ring-red-500`
        : `${baseClass} border-gray-300 focus:border-emerald-500 focus:ring-emerald-500`;
    };

    const getSelectClass = () => {
      return "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed";
    };

    const handleCountryChange = async (countryCode: string) => {
      const country = countries.find((item) => item.code === countryCode);
      setSelectedCountryCode(countryCode);
      setSelectedStateCode("");
      setStates([]);
      setCities([]);
      setFormData((prev: ProfileFormData) => ({
        ...prev,
        country_code: country?.phone_code || "",
      }));
      updateLocationFields({
        country: country?.name || "",
        state: "",
        city: "",
      });

      if (!countryCode) {
        return;
      }

      const statesData = await loadStatesForCountry(countryCode);
      if (statesData.length === 0) {
        await loadCitiesForCountry(countryCode);
      }
    };

    const handleStateChange = async (stateCode: string) => {
      const state = states.find((item) => item.code === stateCode);
      setSelectedStateCode(stateCode);
      setCities([]);
      updateLocationFields({ state: state?.name || "", city: "" });

      if (selectedCountryCode && stateCode) {
        await loadCitiesForState(selectedCountryCode, stateCode);
      }
    };

    const handleCityChange = (cityName: string) => {
      updateLocationFields({ city: cityName });
    };

    const cityOptions =
      formData.city && !cities.some((city) => city.name === formData.city)
        ? [{ id: formData.city, name: formData.city }, ...cities]
        : cities;

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900">
          Personal Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.first_name || ""}
              onChange={(e) => handleChange("first_name", e.target.value)}
              onBlur={(e) => handleBlur("first_name", e.target.value)}
              className={getInputClass("first_name")}
              required
            />
            {errors.first_name && touchedFields.has("first_name") && (
              <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.last_name || ""}
              onChange={(e) => handleChange("last_name", e.target.value)}
              onBlur={(e) => handleBlur("last_name", e.target.value)}
              className={getInputClass("last_name")}
              required
            />
            {errors.last_name && touchedFields.has("last_name") && (
              <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.location || ""}
              onBlur={(e) => handleBlur("location", e.target.value)}
              className={`${getInputClass(
                "location",
              )} bg-gray-50 cursor-not-allowed`}
              placeholder="City, State, Country"
              readOnly
            />
            {errors.location && touchedFields.has("location") && (
              <p className="mt-1 text-sm text-red-600">{errors.location}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCountryCode}
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={loadingLocations.countries}
              className={getSelectClass()}
            >
              <option value="">
                {loadingLocations.countries
                  ? "Loading countries..."
                  : "Select country"}
              </option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Country Code
                </label>
                <input
                  type="text"
                  value={formData.country_code || ""}
                  onChange={(e) =>
                    handleChange("country_code", e.target.value)
                  }
                  onBlur={(e) => handleBlur("country_code", e.target.value)}
                  className={getInputClass("country_code")}
                  placeholder="+1"
                  inputMode="tel"
                  pattern="[0-9+]*"
                />
                {errors.country_code && touchedFields.has("country_code") && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.country_code}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  onBlur={(e) => handleBlur("phone", e.target.value)}
                  className={getInputClass("phone")}
                  placeholder="5551234567"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {errors.phone && touchedFields.has("phone") && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              State
            </label>
            <select
              value={selectedStateCode}
              onChange={(e) => handleStateChange(e.target.value)}
              disabled={
                !selectedCountryCode ||
                loadingLocations.states ||
                states.length === 0
              }
              className={getSelectClass()}
            >
              <option value="">
                {loadingLocations.states
                  ? "Loading states..."
                  : states.length === 0 && selectedCountryCode
                  ? "No states available"
                  : "Select state"}
              </option>
              {states.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              City
            </label>
            <select
              value={formData.city || ""}
              onChange={(e) => handleCityChange(e.target.value)}
              disabled={
                !selectedCountryCode ||
                (states.length > 0 && !selectedStateCode) ||
                loadingLocations.cities
              }
              className={getSelectClass()}
            >
              <option value="">
                {loadingLocations.cities ? "Loading cities..." : "Select city"}
              </option>
              {cityOptions.map((city) => (
                <option key={city.id || city.name} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {locationLoadError && (
          <p className="text-sm text-red-600">{locationLoadError}</p>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Professional Summary <span className="text-red-500">*</span>
            <span className="text-gray-500 text-xs ml-2">
              (Min 100, max 1000 characters)
            </span>
          </label>
          <textarea
            rows={4}
            value={formData.professional_summary || ""}
            onChange={(e) =>
              handleChange("professional_summary", e.target.value)
            }
            onBlur={(e) => handleBlur("professional_summary", e.target.value)}
            className={getInputClass("professional_summary")}
            placeholder="Brief overview of your professional background and career objectives..."
            maxLength={1000}
          />
          {errors.professional_summary &&
            touchedFields.has("professional_summary") && (
              <p className="mt-1 text-sm text-red-600">
                {errors.professional_summary}
              </p>
            )}
          <div className="mt-1 text-sm text-gray-500 text-right">
            {formData.professional_summary?.length || 0}/1000
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Current Job Title
            </label>
            <input
              type="text"
              value={formData.current_job_title || ""}
              onChange={(e) =>
                handleChange("current_job_title", e.target.value)
              }
              onBlur={(e) => handleBlur("current_job_title", e.target.value)}
              className={getInputClass("current_job_title")}
            />
            {errors.current_job_title &&
              touchedFields.has("current_job_title") && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.current_job_title}
                </p>
              )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Current Company
            </label>
            <input
              type="text"
              value={formData.current_company || ""}
              onChange={(e) => handleChange("current_company", e.target.value)}
              onBlur={(e) => handleBlur("current_company", e.target.value)}
              className={getInputClass("current_company")}
            />
            {errors.current_company && touchedFields.has("current_company") && (
              <p className="mt-1 text-sm text-red-600">
                {errors.current_company}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Years of Experience
            </label>
            <input
              type="number"
              min="0"
              max="70"
              step="0.5"
              value={formData.years_of_experience || ""}
              onChange={(e) =>
                handleChange(
                  "years_of_experience",
                  e.target.value ? parseFloat(e.target.value) : "",
                )
              }
              onBlur={(e) =>
                handleBlur(
                  "years_of_experience",
                  e.target.value ? parseFloat(e.target.value) : "",
                )
              }
              className={getInputClass("years_of_experience")}
            />
            {errors.years_of_experience &&
              touchedFields.has("years_of_experience") && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.years_of_experience}
                </p>
              )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              LinkedIn URL
            </label>
            <input
              type="url"
              value={formData.linkedin_url || ""}
              onChange={(e) => handleChange("linkedin_url", e.target.value)}
              onBlur={(e) => handleBlur("linkedin_url", e.target.value)}
              className={getInputClass("linkedin_url")}
              placeholder="https://linkedin.com/in/username"
            />
            {errors.linkedin_url && touchedFields.has("linkedin_url") && (
              <p className="mt-1 text-sm text-red-600">{errors.linkedin_url}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              GitHub URL
            </label>
            <input
              type="url"
              value={formData.github_url || ""}
              onChange={(e) => handleChange("github_url", e.target.value)}
              onBlur={(e) => handleBlur("github_url", e.target.value)}
              className={getInputClass("github_url")}
              placeholder="https://github.com/username"
            />
            {errors.github_url && touchedFields.has("github_url") && (
              <p className="mt-1 text-sm text-red-600">{errors.github_url}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Portfolio URL
            </label>
            <input
              type="url"
              value={formData.portfolio_url || ""}
              onChange={(e) => handleChange("portfolio_url", e.target.value)}
              onBlur={(e) => handleBlur("portfolio_url", e.target.value)}
              className={getInputClass("portfolio_url")}
              placeholder="https://yourportfolio.com"
            />
            {errors.portfolio_url && touchedFields.has("portfolio_url") && (
              <p className="mt-1 text-sm text-red-600">
                {errors.portfolio_url}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Personal Website
            </label>
            <input
              type="url"
              value={formData.personal_website || ""}
              onChange={(e) => handleChange("personal_website", e.target.value)}
              onBlur={(e) => handleBlur("personal_website", e.target.value)}
              className={getInputClass("personal_website")}
              placeholder="https://yourwebsite.com"
            />
            {errors.personal_website &&
              touchedFields.has("personal_website") && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.personal_website}
                </p>
              )}
          </div>
        </div>
      </div>
    );
  },
);

// Skills Tab Component
const SkillsTab: React.FC<any> = ({
  skills,
  addItem,
  updateItem,
  deleteItem,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSkill, setNewSkill] = useState({
    skill_name: "",
    proficiency_level: "intermediate",
    years_of_experience: 0,
  });

  const handleAddSkill = () => {
    if (newSkill.skill_name.trim()) {
      addItem("skills", newSkill);
      setNewSkill({
        skill_name: "",
        proficiency_level: "intermediate",
        years_of_experience: 0,
      });
      setShowAddForm(false);
    }
  };

  const handleCancel = () => {
    setNewSkill({
      skill_name: "",
      proficiency_level: "intermediate",
      years_of_experience: 0,
    });
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Skills</h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
        >
          Add Skill
        </button>
      </div>

      {/* Add Skill Form */}
      {showAddForm && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-3">Add New Skill</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-700">
                Skill Name
              </label>
              <input
                type="text"
                value={newSkill.skill_name}
                onChange={(e) =>
                  setNewSkill({ ...newSkill, skill_name: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., JavaScript, Python, Project Management"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700">
                Proficiency Level
              </label>
              <select
                value={newSkill.proficiency_level}
                onChange={(e) =>
                  setNewSkill({
                    ...newSkill,
                    proficiency_level: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700">
                Years of Experience
              </label>
              <input
                type="number"
                min="0"
                value={newSkill.years_of_experience}
                onChange={(e) =>
                  setNewSkill({
                    ...newSkill,
                    years_of_experience: parseInt(e.target.value) || 0,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleAddSkill}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            >
              Add Skill
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Skills List */}
      <div className="space-y-4">
        {skills.map((skill: Skill, index: number) => (
          <div
            key={skill.id || index}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Skill Name
                  </label>
                  <input
                    type="text"
                    value={skill.skill_name}
                    onChange={(e) =>
                      updateItem("skills", index, {
                        ...skill,
                        skill_name: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Proficiency
                  </label>
                  <select
                    value={skill.proficiency_level}
                    onChange={(e) =>
                      updateItem("skills", index, {
                        ...skill,
                        proficiency_level: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Years Experience
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={skill.years_of_experience || 0}
                    onChange={(e) =>
                      updateItem("skills", index, {
                        ...skill,
                        years_of_experience: parseInt(e.target.value) || 0,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => deleteItem("skills", index)}
                className="ml-4 p-2 text-red-600 hover:text-red-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}

        {skills.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No skills added yet. Click "Add Skill" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Additional tab components would be defined here...
// For brevity, I'll add placeholders for the other tabs

const ExperienceTab: React.FC<any> = ({
  experiences,
  addItem,
  updateItem,
  deleteItem,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newExperience, setNewExperience] = useState({
    job_title: "",
    company_name: "",
    location: "",
    start_date: "",
    end_date: "",
    is_current: false,
    description: "",
    responsibilities: [],
    key_achievements: [],
  });
  const [errors, setErrors] = useState<any>({});
  const handleAddExperience = () => {
    const validationErrors = validateSingleExperience(newExperience);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    if (newExperience.job_title.trim() && newExperience.company_name.trim()) {
      addItem("work_experiences", newExperience);
      setNewExperience({
        job_title: "",
        company_name: "",
        location: "",
        start_date: "",
        end_date: "",
        is_current: false,
        description: "",
        responsibilities: [],
        key_achievements: [],
      });
      setShowAddForm(false);
      setErrors({}); // Clear errors on successful submission
    }
  };

  const handleCancel = () => {
    setNewExperience({
      job_title: "",
      company_name: "",
      location: "",
      start_date: "",
      end_date: "",
      is_current: false,
      description: "",
      responsibilities: [],
      key_achievements: [],
    });
    setShowAddForm(false);
    setErrors({}); // Clear errors on cancel
  };

  const handleAchievementAdd = (expIndex: number, newAchievement: string) => {
    if (newAchievement.trim()) {
      const updatedExperience = { ...experiences[expIndex] };
      updatedExperience.key_achievements = [
        ...(updatedExperience.key_achievements || []),
        newAchievement.trim(),
      ];
      updateItem("work_experiences", expIndex, updatedExperience);
    }
  };

  const handleAchievementRemove = (
    expIndex: number,
    achievementIndex: number,
  ) => {
    const updatedExperience = { ...experiences[expIndex] };
    updatedExperience.key_achievements =
      updatedExperience.key_achievements.filter(
        (_: any, i: number) => i !== achievementIndex,
      );
    updateItem("work_experiences", expIndex, updatedExperience);
  };

  const handleResponsibilityAdd = (
    expIndex: number,
    newResponsibility: string,
  ) => {
    if (newResponsibility.trim()) {
      const updatedExperience = { ...experiences[expIndex] };
      updatedExperience.responsibilities = [
        ...(updatedExperience.responsibilities || []),
        newResponsibility.trim(),
      ];
      updateItem("work_experiences", expIndex, updatedExperience);
    }
  };

  const handleResponsibilityRemove = (
    expIndex: number,
    responsibilityIndex: number,
  ) => {
    const updatedExperience = { ...experiences[expIndex] };
    if (
      updatedExperience.responsibilities &&
      updatedExperience.responsibilities.length > 0
    ) {
      updatedExperience.responsibilities =
        updatedExperience.responsibilities.filter(
          (_: any, i: number) => i !== responsibilityIndex,
        );
      updateItem("work_experiences", expIndex, updatedExperience);
    }
  };

  // Helper function to get field error
  const getFieldError = (fieldName: string) => {
    return errors[fieldName] ? (
      <p className="mt-1 text-sm text-red-600">{errors[fieldName]}</p>
    ) : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Work Experience</h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
        >
          Add Experience
        </button>
      </div>

      {/* Add Experience Form */}
      {showAddForm && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-3">Add New Experience</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={newExperience.job_title}
                  onChange={(e) => {
                    const updated = {
                      ...newExperience,
                      job_title: e.target.value,
                    };
                    setNewExperience(updated);
                    setErrors(validateSingleExperience(updated));
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    errors.job_title
                      ? "border-red-300 focus:border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                  placeholder="e.g., Senior Software Engineer"
                />
                {getFieldError("job_title")}
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-700">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={newExperience.company_name}
                  onChange={(e) => {
                    const updated = {
                      ...newExperience,
                      company_name: e.target.value,
                    };
                    setNewExperience(updated);
                    setErrors(validateSingleExperience(updated));
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    errors.company_name
                      ? "border-red-300 focus:border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                  placeholder="e.g., Tech Corp Inc."
                />
                {getFieldError("company_name")}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700">
                Location
              </label>
              <input
                type="text"
                value={newExperience.location}
                onChange={(e) =>
                  setNewExperience({
                    ...newExperience,
                    location: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., San Francisco, CA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700">
                Job Description
              </label>
              <textarea
                rows={3}
                value={newExperience.description}
                onChange={(e) =>
                  setNewExperience({
                    ...newExperience,
                    description: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Brief description of your role and responsibilities..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700">
                  Start Date *
                </label>
                <MonthInput
                  value={newExperience.start_date}
                  max={currentMonth}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = {
                      ...newExperience,
                      start_date: value,
                    };
                    setNewExperience(updated);
                    setErrors(validateSingleExperience(updated));
                  }}
                  className={`block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    errors.start_date
                      ? "border-red-300 focus:border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {getFieldError("start_date")}
              </div>

              <div className="flex items-center">
                <input
                  id="new_exp_current"
                  type="checkbox"
                  checked={newExperience.is_current}
                  onChange={(e) => {
                    const updated = {
                      ...newExperience,
                      is_current: e.target.checked,
                      end_date: e.target.checked ? "" : newExperience.end_date,
                    };
                    setNewExperience(updated);
                    setErrors(validateSingleExperience(updated));
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="new_exp_current"
                  className="ml-2 block text-sm text-blue-700"
                >
                  This is my current job
                </label>
              </div>
            </div>

            {!newExperience.is_current && (
              <div>
                <label className="block text-sm font-medium text-blue-700">
                  End Date *
                </label>
                <MonthInput
                  value={newExperience.end_date}
                  min={newExperience.start_date}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = {
                      ...newExperience,
                      end_date: value,
                    };
                    setNewExperience(updated);
                    setErrors(validateSingleExperience(updated));
                  }}
                  className={`block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    errors.end_date
                      ? "border-red-300 focus:border-red-500"
                      : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {getFieldError("end_date")}
              </div>
            )}
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleAddExperience}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            >
              Add Experience
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Experience List */}
      <div className="space-y-6">
        {experiences.map((experience: WorkExperience, index: number) => (
          <div
            key={experience.id || `exp-${index}`}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Job Title
                    </label>
                    <input
                      type="text"
                      value={experience.job_title}
                      onChange={(e) =>
                        updateItem("work_experiences", index, {
                          ...experience,
                          job_title: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={experience.company_name}
                      onChange={(e) =>
                        updateItem("work_experiences", index, {
                          ...experience,
                          company_name: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input
                    type="text"
                    value={experience.location || ""}
                    onChange={(e) =>
                      updateItem("work_experiences", index, {
                        ...experience,
                        location: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Job Description
                  </label>
                  <textarea
                    rows={3}
                    value={experience.description || ""}
                    onChange={(e) =>
                      updateItem("work_experiences", index, {
                        ...experience,
                        description: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Start Date
                    </label>
                    <MonthInput
                      value={formatDateForMonthInput(experience.start_date)}
                      max={currentMonth}
                      onChange={(e) =>
                        updateItem("work_experiences", index, {
                          ...experience,
                          start_date: e.target.value,
                        })
                      }
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      id={`exp_current_${index}`}
                      type="checkbox"
                      checked={experience.is_current || false}
                      onChange={(e) =>
                        updateItem("work_experiences", index, {
                          ...experience,
                          is_current: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`exp_current_${index}`}
                      className="ml-2 block text-sm text-gray-700"
                    >
                      This is my current job
                    </label>
                  </div>
                </div>

                {!experience.is_current && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <MonthInput
                      value={formatDateForMonthInput(experience.end_date)}
                      min={experience.start_date}
                      onChange={(e) =>
                        updateItem("work_experiences", index, {
                          ...experience,
                          end_date: e.target.value,
                        })
                      }
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                )}

                {/* Responsibilities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Responsibilities
                  </label>
                  <textarea
                    rows={4}
                    defaultValue={(experience.responsibilities || []).join(
                      "\n",
                    )}
                    onBlur={(e) => {
                      const lines = e.target.value
                        .split("\n")
                        .map((line) => line.trim())
                        .filter((line) => line.length > 0);
                      updateItem("work_experiences", index, {
                        ...experience,
                        responsibilities: lines,
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    placeholder="Enter each responsibility on a new line"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter each responsibility on a new line. Press Enter to
                    create a new line.
                  </p>
                </div>

                {/* Key Achievements */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Achievements
                  </label>
                  <textarea
                    rows={4}
                    defaultValue={(experience.key_achievements || []).join(
                      "\n",
                    )}
                    onBlur={(e) => {
                      const lines = e.target.value
                        .split("\n")
                        .map((line) => line.trim())
                        .filter((line) => line.length > 0);
                      updateItem("work_experiences", index, {
                        ...experience,
                        key_achievements: lines,
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    placeholder="Enter each achievement on a new line"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter each achievement on a new line. Press Enter to create
                    a new line.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => deleteItem("work_experiences", index)}
                className="ml-4 p-2 text-red-600 hover:text-red-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}

        {experiences.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>
              No work experience added yet. Click "Add Experience" to get
              started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const EducationTab: React.FC<any> = ({
  education,
  educationError,
  addItem,
  updateItem,
  deleteItem,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEducation, setNewEducation] = useState({
    degree: "",
    field_of_study: "",
    institution: "",
    graduation_year: "",
    gpa: "",
  });

  const handleAddEducation = () => {
    if (newEducation.degree.trim() && newEducation.institution.trim()) {
      const educationData = {
        ...newEducation,
        graduation_year: newEducation.graduation_year
          ? parseInt(newEducation.graduation_year)
          : undefined,
        gpa: newEducation.gpa ? parseFloat(newEducation.gpa) : undefined,
      };
      addItem("education", educationData);
      setNewEducation({
        degree: "",
        field_of_study: "",
        institution: "",
        graduation_year: "",
        gpa: "",
      });
      setShowAddForm(false);
    }
  };

  const handleCancel = () => {
    setNewEducation({
      degree: "",
      field_of_study: "",
      institution: "",
      graduation_year: "",
      gpa: "",
    });
    setShowAddForm(false);
  };
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          Education <span className="text-red-500">*</span>
        </h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
        >
          Add Education
        </button>
      </div>
      {educationError && (
        <p className="text-sm text-red-600">{educationError}</p>
      )}

      {/* Add Education Form */}
      {showAddForm && (
        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
          <h4 className="font-medium text-indigo-900 mb-3">
            Add New Education
          </h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-indigo-700">
                  Degree <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newEducation.degree}
                  onChange={(e) =>
                    setNewEducation({ ...newEducation, degree: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="e.g., Bachelor of Science, Master of Arts"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-indigo-700">
                  Field of Study
                </label>
                <input
                  type="text"
                  value={newEducation.field_of_study}
                  onChange={(e) =>
                    setNewEducation({
                      ...newEducation,
                      field_of_study: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="e.g., Computer Science, Business Administration"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-indigo-700">
                Institution <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newEducation.institution}
                onChange={(e) =>
                  setNewEducation({
                    ...newEducation,
                    institution: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="e.g., University of California, Berkeley"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-indigo-700">
                  Graduation Year
                </label>
                <input
                  type="number"
                  value={newEducation.graduation_year}
                  onChange={(e) =>
                    setNewEducation({
                      ...newEducation,
                      graduation_year: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="2023"
                  min="1950"
                  max="2030"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-indigo-700">
                  GPA (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newEducation.gpa}
                  onChange={(e) =>
                    setNewEducation({ ...newEducation, gpa: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="3.75"
                  min="0"
                  max="4"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleAddEducation}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
            >
              Add Education
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Education List */}
      <div className="space-y-6">
        {education.map((edu: Education, index: number) => (
          <div
            key={edu.id || `edu-${index}`}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Degree <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={edu.degree}
                      onChange={(e) =>
                        updateItem("education", index, {
                          ...edu,
                          degree: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Field of Study
                    </label>
                    <input
                      type="text"
                      value={edu.field_of_study || ""}
                      onChange={(e) =>
                        updateItem("education", index, {
                          ...edu,
                          field_of_study: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Institution <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={edu.institution}
                    onChange={(e) =>
                      updateItem("education", index, {
                        ...edu,
                        institution: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Graduation Year
                    </label>
                    <input
                      type="number"
                      value={edu.graduation_year || ""}
                      onChange={(e) =>
                        updateItem("education", index, {
                          ...edu,
                          graduation_year: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      min="1950"
                      max="2030"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      GPA
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={edu.gpa || ""}
                      onChange={(e) =>
                        updateItem("education", index, {
                          ...edu,
                          gpa: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      min="0"
                      max="4"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => deleteItem("education", index)}
                className="ml-4 p-2 text-red-600 hover:text-red-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}

        {education.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No education added yet. Click "Add Education" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectsTab: React.FC<any> = ({
  projects,
  addItem,
  updateItem,
  deleteItem,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    role: "",
    technologies: [],
    url: "",
    start_date: "",
    end_date: "",
    is_ongoing: false,
    achievements: [],
  });
  const [errors, setErrors] = useState<any>({});
  const handleAddProject = () => {
    const validationErrors = validateSingleProject(newProject);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;
    if (newProject.name.trim()) {
      addItem("projects", newProject);
      setNewProject({
        name: "",
        description: "",
        role: "",
        technologies: [],
        url: "",
        start_date: "",
        end_date: "",
        is_ongoing: false,
        achievements: [],
      });
      setShowAddForm(false);
      setErrors({});
    }
  };
  const handleCancel = () => {
    setNewProject({
      name: "",
      description: "",
      role: "",
      technologies: [],
      url: "",
      start_date: "",
      end_date: "",
      is_ongoing: false,
      achievements: [],
    });
    setShowAddForm(false);
    setErrors({});
  };

  const handleTechnologyAdd = (projectIndex: number, newTech: string) => {
    if (newTech.trim()) {
      const updatedProject = { ...projects[projectIndex] };
      updatedProject.technologies = [
        ...(updatedProject.technologies || []),
        newTech.trim(),
      ];
      updateItem("projects", projectIndex, updatedProject);
    }
  };

  const handleTechnologyRemove = (projectIndex: number, techIndex: number) => {
    const updatedProject = { ...projects[projectIndex] };
    updatedProject.technologies = updatedProject.technologies.filter(
      (_: string, i: number) => i !== techIndex,
    );
    updateItem("projects", projectIndex, updatedProject);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Projects</h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700"
        >
          Add Project
        </button>
      </div>

      {/* Add Project Form */}
      {showAddForm && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <h4 className="font-medium text-purple-900 mb-3">Add New Project</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-purple-700">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => {
                    const updated = { ...newProject, name: e.target.value };
                    setNewProject(updated);
                    setErrors(validateSingleProject(updated));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  placeholder="e.g., E-commerce Platform"
                />
                {errors.project_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.project_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-700">
                  Your Role
                </label>
                <input
                  type="text"
                  value={newProject.role}
                  onChange={(e) =>
                    setNewProject({ ...newProject, role: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  placeholder="e.g., Lead Developer, Project Manager"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700">
                Description
              </label>
              <textarea
                rows={3}
                value={newProject.description}
                onChange={(e) =>
                  setNewProject({ ...newProject, description: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                placeholder="Brief description of the project and your contributions..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-purple-700">
                  Project URL
                </label>
                <input
                  type="url"
                  value={newProject.url}
                  onChange={(e) =>
                    setNewProject({ ...newProject, url: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  placeholder="https://github.com/username/project"
                />
              </div>

              <div className="flex items-center">
                <input
                  id="new_project_ongoing"
                  type="checkbox"
                  checked={newProject.is_ongoing}
                  onChange={(e) => {
                    const updated = {
                      ...newProject,
                      is_ongoing: e.target.checked,
                      end_date: e.target.checked ? "" : newProject.end_date,
                    };
                    setNewProject(updated);
                    setErrors(validateSingleProject(updated));
                  }}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />

                <label
                  htmlFor="new_project_ongoing"
                  className="ml-2 block text-sm text-purple-700"
                >
                  This project is ongoing
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-purple-700">
                  Start Date *
                </label>
                <MonthInput
                  value={newProject.start_date}
                  max={currentMonth}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = {
                      ...newProject,
                      start_date: value,
                    };
                    setNewProject(updated);
                    setErrors(validateSingleProject(updated));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
                {errors.start_date && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.start_date}
                  </p>
                )}
              </div>

              {!newProject.is_ongoing && (
                <div>
                  <label className="block text-sm font-medium text-purple-700">
                    End Date*
                  </label>
                  <MonthInput
                    value={newProject.end_date}
                    min={newProject.start_date}
                    onChange={(e) => {
                      const value = e.target.value;
                      const updated = {
                        ...newProject,
                        end_date: value,
                      };
                      setNewProject(updated);
                      setErrors(validateSingleProject(updated));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />

                  {errors.end_date && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.end_date}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleAddProject}
              className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700"
            >
              Add Project
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Projects List */}
      <div className="space-y-6">
        {projects.map((project: Project, index: number) => (
          <div
            key={project.id || index}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={project.name}
                    onChange={(e) =>
                      updateItem("projects", index, {
                        ...project,
                        name: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Your Role
                  </label>
                  <input
                    type="text"
                    value={project.role || ""}
                    onChange={(e) =>
                      updateItem("projects", index, {
                        ...project,
                        role: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => deleteItem("projects", index)}
                className="ml-4 p-2 text-red-600 hover:text-red-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={project.description || ""}
                  onChange={(e) =>
                    updateItem("projects", index, {
                      ...project,
                      description: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Project URL
                  </label>
                  <input
                    type="url"
                    value={project.url || ""}
                    onChange={(e) =>
                      updateItem("projects", index, {
                        ...project,
                        url: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    id={`project_ongoing_${index}`}
                    type="checkbox"
                    checked={project.is_ongoing || false}
                    onChange={(e) =>
                      updateItem("projects", index, {
                        ...project,
                        is_ongoing: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`project_ongoing_${index}`}
                    className="ml-2 block text-sm text-gray-700"
                  >
                    This project is ongoing
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Start Date
                  </label>
                  <MonthInput
                    value={formatDateForMonthInput(project.start_date)}
                    onChange={(e) =>
                      updateItem("projects", index, {
                        ...project,
                        start_date: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                {!project.is_ongoing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <MonthInput
                      value={formatDateForMonthInput(project.end_date)}
                      onChange={(e) =>
                        updateItem("projects", index, {
                          ...project,
                          end_date: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>

              {/* Technologies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technologies Used
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(project.technologies || []).map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tech}
                      <button
                        type="button"
                        onClick={() => handleTechnologyRemove(index, techIndex)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Add technology (press Enter)"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleTechnologyAdd(index, e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                />
              </div>
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No projects added yet. Click "Add Project" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const CertificationsTab: React.FC<any> = ({
  certifications,
  addItem,
  updateItem,
  deleteItem,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCertification, setNewCertification] = useState({
    name: "",
    issuing_organization: "",
    issue_date: "",
    expiration_date: "",
    credential_id: "",
    credential_url: "",
    does_not_expire: false,
  });
  const [errors, setErrors] = useState<any>({});

  const handleAddCertification = () => {
    const validationErrors = validateSingleCertification(newCertification);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (
      newCertification.name.trim() &&
      newCertification.issuing_organization.trim()
    ) {
      addItem("certifications", newCertification);
      setNewCertification({
        name: "",
        issuing_organization: "",
        issue_date: "",
        expiration_date: "",
        credential_id: "",
        credential_url: "",
        does_not_expire: false,
      });
      setShowAddForm(false);
    }
  };

  const handleCancel = () => {
    setNewCertification({
      name: "",
      issuing_organization: "",
      issue_date: "",
      expiration_date: "",
      credential_id: "",
      credential_url: "",
      does_not_expire: false,
    });
    setShowAddForm(false);
  };
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Certifications</h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-yellow-600 text-white rounded-md text-sm font-medium hover:bg-yellow-700"
        >
          Add Certification
        </button>
      </div>

      {/* Add Certification Form */}
      {showAddForm && (
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h4 className="font-medium text-yellow-900 mb-3">
            Add New Certification
          </h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-yellow-700">
                  Certification Name *
                </label>
                <input
                  type="text"
                  value={newCertification.name}
                  onChange={(e) => {
                    const updated = {
                      ...newCertification,
                      name: e.target.value,
                    };
                    setNewCertification(updated);
                    setErrors(validateSingleCertification(updated));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., AWS Certified Solutions Architect"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-yellow-700">
                  Issuing Organization *
                </label>
                <input
                  type="text"
                  value={newCertification.issuing_organization}
                  onChange={(e) => {
                    const updated = {
                      ...newCertification,
                      issuing_organization: e.target.value,
                    };
                    setNewCertification(updated);
                    setErrors(validateSingleCertification(updated));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., Amazon Web Services"
                />
                {errors.issuing_organization && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.issuing_organization}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-yellow-700">
                  Credential ID (Optional)
                </label>
                <input
                  type="text"
                  value={newCertification.credential_id}
                  onChange={(e) =>
                    setNewCertification({
                      ...newCertification,
                      credential_id: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., ABC123XYZ"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-yellow-700">
                  Credential URL (Optional)
                </label>
                <input
                  type="url"
                  value={newCertification.credential_url}
                  onChange={(e) =>
                    setNewCertification({
                      ...newCertification,
                      credential_url: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="https://www.credly.com/badges/..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-yellow-700">
                  Issue Date *
                </label>
                <MonthInput
                  value={newCertification.issue_date}
                  max={currentMonth}
                  onChange={(e) => {
                    const value = e.target.value;

                    const updated = {
                      ...newCertification,
                      issue_date: value,
                    };

                    setNewCertification(updated);
                    setErrors(validateSingleCertification(updated));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                />
                {errors.issue_date && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.issue_date}
                  </p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  id="new_cert_no_expire"
                  type="checkbox"
                  checked={newCertification.does_not_expire}
                  onChange={(e) =>
                    setNewCertification({
                      ...newCertification,
                      does_not_expire: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="new_cert_no_expire"
                  className="ml-2 block text-sm text-yellow-700"
                >
                  This certification does not expire
                </label>
              </div>
            </div>

            {!newCertification.does_not_expire && (
              <div>
                <label className="block text-sm font-medium text-yellow-700">
                  Expiration Date *
                </label>
                <MonthInput
                  value={newCertification.expiration_date}
                  min={newCertification.issue_date}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = {
                      ...newCertification,
                      expiration_date: value,
                    };

                    setNewCertification(updated);
                    setErrors(validateSingleCertification(updated));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                />
                {errors.expiration_date && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.expiration_date}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleAddCertification}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md text-sm font-medium hover:bg-yellow-700"
            >
              Add Certification
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Certifications List */}
      <div className="space-y-6">
        {certifications.map((certification: Certification, index: number) => (
          <div
            key={certification.id || `cert-${index}`}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Certification Name
                    </label>
                    <input
                      type="text"
                      value={certification.name}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          name: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Issuing Organization
                    </label>
                    <input
                      type="text"
                      value={certification.issuing_organization}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          issuing_organization: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Credential ID
                    </label>
                    <input
                      type="text"
                      value={certification.credential_id || ""}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          credential_id: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Credential URL
                    </label>
                    <input
                      type="url"
                      value={certification.credential_url || ""}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          credential_url: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Issue Date
                    </label>
                    <MonthInput
                      value={formatDateForMonthInput(certification.issue_date)}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          issue_date: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      id={`cert_no_expire_${index}`}
                      type="checkbox"
                      checked={certification.does_not_expire || false}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          does_not_expire: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`cert_no_expire_${index}`}
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Does not expire
                    </label>
                  </div>
                </div>

                {!certification.does_not_expire && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Expiration Date
                    </label>
                    <MonthInput
                      value={formatDateForMonthInput(
                        certification.expiration_date,
                      )}
                      onChange={(e) =>
                        updateItem("certifications", index, {
                          ...certification,
                          expiration_date: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => deleteItem("certifications", index)}
                className="ml-4 p-2 text-red-600 hover:text-red-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}

        {certifications.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>
              No certifications added yet. Click "Add Certification" to get
              started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const LanguagesAwardsTab: React.FC<any> = ({
  languages,
  awards,
  addItem,
  updateItem,
  deleteItem,
}) => {
  const [showAddLanguageForm, setShowAddLanguageForm] = useState(false);
  const [showAddAwardForm, setShowAddAwardForm] = useState(false);
  const [newLanguage, setNewLanguage] = useState({
    language: "",
    proficiency: "intermediate",
    is_native: false,
  });
  const [newAward, setNewAward] = useState({
    title: "",
    organization: "",
    date: "",
    description: "",
  });

  const handleAddLanguage = () => {
    if (newLanguage.language.trim()) {
      addItem("languages", newLanguage);
      setNewLanguage({
        language: "",
        proficiency: "intermediate",
        is_native: false,
      });
      setShowAddLanguageForm(false);
    }
  };

  const handleAddAward = () => {
    if (newAward.title.trim() && newAward.organization.trim()) {
      addItem("awards", newAward);
      setNewAward({
        title: "",
        organization: "",
        date: "",
        description: "",
      });
      setShowAddAwardForm(false);
    }
  };

  const handleCancelLanguage = () => {
    setNewLanguage({
      language: "",
      proficiency: "intermediate",
      is_native: false,
    });
    setShowAddLanguageForm(false);
  };

  const handleCancelAward = () => {
    setNewAward({
      title: "",
      organization: "",
      date: "",
      description: "",
    });
    setShowAddAwardForm(false);
  };

  return (
    <div className="space-y-8">
      {/* Languages Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Languages</h3>
          <button
            type="button"
            onClick={() => setShowAddLanguageForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
          >
            Add Language
          </button>
        </div>

        {/* Add Language Form */}
        {showAddLanguageForm && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-medium text-green-900 mb-3">
              Add New Language
            </h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-green-700">
                    Language
                  </label>
                  <input
                    type="text"
                    value={newLanguage.language}
                    onChange={(e) =>
                      setNewLanguage({
                        ...newLanguage,
                        language: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    placeholder="e.g., Spanish, French, Mandarin"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-green-700">
                    Proficiency Level
                  </label>
                  <select
                    value={newLanguage.proficiency}
                    onChange={(e) =>
                      setNewLanguage({
                        ...newLanguage,
                        proficiency: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="fluent">Fluent</option>
                    <option value="native">Native</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="new_lang_native"
                  type="checkbox"
                  checked={newLanguage.is_native}
                  onChange={(e) =>
                    setNewLanguage({
                      ...newLanguage,
                      is_native: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="new_lang_native"
                  className="ml-2 block text-sm text-green-700"
                >
                  This is my native language
                </label>
              </div>
            </div>

            <div className="mt-4 flex space-x-3">
              <button
                type="button"
                onClick={handleAddLanguage}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
              >
                Add Language
              </button>
              <button
                type="button"
                onClick={handleCancelLanguage}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Languages List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {languages.map((language: Language, index: number) => (
            <div
              key={language.id || `lang-${index}`}
              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Language
                    </label>
                    <input
                      type="text"
                      value={language.language}
                      onChange={(e) =>
                        updateItem("languages", index, {
                          ...language,
                          language: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Proficiency
                    </label>
                    <select
                      value={language.proficiency}
                      onChange={(e) =>
                        updateItem("languages", index, {
                          ...language,
                          proficiency: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="fluent">Fluent</option>
                      <option value="native">Native</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      id={`lang_native_${index}`}
                      type="checkbox"
                      checked={language.is_native || false}
                      onChange={(e) =>
                        updateItem("languages", index, {
                          ...language,
                          is_native: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`lang_native_${index}`}
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Native language
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => deleteItem("languages", index)}
                  className="ml-4 p-2 text-red-600 hover:text-red-800"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {languages.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p>No languages added yet. Click "Add Language" to get started.</p>
          </div>
        )}
      </div>

      {/* Awards Section */}
      <div className="space-y-6 border-t pt-8">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Awards & Honors</h3>
          <button
            type="button"
            onClick={() => setShowAddAwardForm(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700"
          >
            Add Award
          </button>
        </div>

        {/* Add Award Form */}
        {showAddAwardForm && (
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <h4 className="font-medium text-orange-900 mb-3">Add New Award</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-orange-700">
                    Award Title
                  </label>
                  <input
                    type="text"
                    value={newAward.title}
                    onChange={(e) =>
                      setNewAward({ ...newAward, title: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    placeholder="e.g., Employee of the Year, Dean's List"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-700">
                    Organization
                  </label>
                  <input
                    type="text"
                    value={newAward.organization}
                    onChange={(e) =>
                      setNewAward({ ...newAward, organization: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    placeholder="e.g., ABC Company, University Name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-orange-700">
                  Date Received
                </label>
                <MonthInput
                  value={newAward.date}
                  onChange={(e) =>
                    setNewAward({ ...newAward, date: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-orange-700">
                  Description (Optional)
                </label>
                <textarea
                  rows={2}
                  value={newAward.description}
                  onChange={(e) =>
                    setNewAward({ ...newAward, description: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                  placeholder="Brief description of the award..."
                />
              </div>
            </div>

            <div className="mt-4 flex space-x-3">
              <button
                type="button"
                onClick={handleAddAward}
                className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700"
              >
                Add Award
              </button>
              <button
                type="button"
                onClick={handleCancelAward}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Awards List */}
        <div className="space-y-4">
          {awards.map((award: Award, index: number) => (
            <div
              key={award.id || `award-${index}`}
              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Award Title
                      </label>
                      <input
                        type="text"
                        value={award.title}
                        onChange={(e) =>
                          updateItem("awards", index, {
                            ...award,
                            title: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Organization
                      </label>
                      <input
                        type="text"
                        value={award.organization}
                        onChange={(e) =>
                          updateItem("awards", index, {
                            ...award,
                            organization: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Date Received
                    </label>
                    <MonthInput
                      value={formatDateForMonthInput(award.date)}
                      onChange={(e) =>
                        updateItem("awards", index, {
                          ...award,
                          date: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      value={award.description || ""}
                      onChange={(e) =>
                        updateItem("awards", index, {
                          ...award,
                          description: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => deleteItem("awards", index)}
                  className="ml-4 p-2 text-red-600 hover:text-red-800"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {awards.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p>No awards added yet. Click "Add Award" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const PreferencesTab: React.FC<any> = ({ formData, setFormData }) => {
  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Work Preferences</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Expected Salary (USD)
          </label>
          <input
            type="number"
            value={formData.expected_salary}
            min={0}
            onChange={(e) => handleChange("expected_salary", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="75000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Availability
          </label>
          <select
            value={formData.availability}
            onChange={(e) => handleChange("availability", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
          >
            <option value="immediately">Immediately</option>
            <option value="2_weeks">2 weeks notice</option>
            <option value="1_month">1 month notice</option>
            <option value="2_months">2 months notice</option>
            <option value="3_months">3+ months</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Preferred Work Type
          </label>
          <select
            value={formData.preferred_work_type}
            onChange={(e) =>
              handleChange("preferred_work_type", e.target.value)
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
          >
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
            <option value="contract">Contract</option>
            <option value="freelance">Freelance</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Work Authorization
          </label>
          <select
            value={formData.work_authorization}
            onChange={(e) => handleChange("work_authorization", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
          >
            <option value="authorized">Authorized to work</option>
            <option value="citizen">US Citizen</option>
            <option value="permanent_resident">Permanent Resident</option>
            <option value="visa_required">Visa sponsorship required</option>
            <option value="student_visa">Student visa (F-1/OPT)</option>
          </select>
        </div>
      </div>

      <div className="flex items-center">
        <input
          id="willing_to_relocate"
          type="checkbox"
          checked={formData.willing_to_relocate}
          onChange={(e) =>
            handleChange("willing_to_relocate", e.target.checked)
          }
          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
        />
        <label
          htmlFor="willing_to_relocate"
          className="ml-2 block text-sm text-gray-900"
        >
          Willing to relocate for the right opportunity
        </label>
      </div>
    </div>
  );
};

export default CandidateProfileEditNew;
